package co.diena.everything.app.worker.util

import co.diena.everything.api.Bucket
import co.diena.everything.api.EntityId
import co.diena.everything.api.EverythingApi
import co.diena.everything.api.EverythingEvent
import co.diena.everything.api.ResolvedCategory
import co.diena.everything.app.worker.EventLoader
import co.diena.everything.app.worker.LoaderError
import co.touchlab.kermit.Logger
import com.github.michaelbull.result.Ok
import com.github.michaelbull.result.combine
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.lortex.lines.common.AnyError
import org.lortex.lines.common.AnyResult
import org.lortex.lines.common.handleError
import org.lortex.lines.common.protectAsync
import org.lortex.lines.common.std.Either
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

private val log = Logger.withTag("ApiEventLoader")

class ApiEventLoader(
    val api: EverythingApi,
    val lang: String,
    val limitPerBucket: Int = 10,
) : EventLoader {
    override val events = MutableStateFlow(emptyMap<EntityId, EverythingEvent>())

    private val coroutineScope = CoroutineScope(Dispatchers.Default)

    override val pendingBucketsCount = MutableStateFlow(0)
    override val pendingEntitiesCount = MutableStateFlow(0)

    val pendingBuckets = MutableStateFlow(emptySet<BucketOptionalCategory>())
    val activeBuckets = MutableStateFlow(emptySet<BucketOptionalCategory>())
    val pendingEntities = MutableStateFlow(emptySet<EntityId>())
    val activeEntities = MutableStateFlow(emptySet<EntityId>())
    internal val entitiesErrors = MutableStateFlow(emptyMap<EntityId, LoaderError>())
    internal val bucketsErrors = MutableStateFlow(emptyMap<BucketOptionalCategory, LoaderError>())

    override val error = kotlinx.coroutines.flow.combine(
        bucketsErrors,
        entitiesErrors
    ) { bucketsErrors, entitiesErrors ->
        bucketsErrors.values.firstOrNull() ?: entitiesErrors.values.firstOrNull()
    }.stateIn(coroutineScope, SharingStarted.Companion.Eagerly, null)

    override val currentCategory = MutableStateFlow<EntityId?>(null)

    override suspend fun setCategory(category: EntityId?) {
        currentCategory.emit(category)
    }

    override val availableCategories: StateFlow<List<ResolvedCategory>> = flow {
        emit(
            protectAsync {
                api.availableCategories(lang)
            }.handleError {
                log.e("Error loading available categories: $it")
                emptyList()
            }
        )
    }.stateIn(
        coroutineScope,
        SharingStarted.Companion.Eagerly,
        emptyList()
    )

    private val bucketLoader: BucketLoader = BucketLoader(
        CoroutineScope(Dispatchers.Default),
        concurrency = 16,
        api,
        limitPerBucket = limitPerBucket
    )

    private val entityLoader = EntityLoader(
        CoroutineScope(Dispatchers.Default),
        concurrency = 16,
        api,
        lang = lang
    )

    val resolvedEntityCachedLoader = CachedLoader(
        entityLoader,
        waitBeforeLoading = 0.seconds
    )
    val bucketEntityCachedLoader = CachedLoader(
        bucketLoader,
        waitBeforeLoading = 200.milliseconds
    )


    init {
        log.i("ApiEventLoader created")
    }


    override suspend fun loadEvents(
        coroutineScope: CoroutineScope,
        buckets: List<Bucket>,
    ) {
        currentCategory.collectLatest { category ->
            val bucketsCategories = buckets.map { bucket ->
                BucketOptionalCategory(bucket, category)
            }
            bucketEntityCachedLoader.setActive(bucketsCategories.toSet())
            activeBuckets.value = bucketsCategories.toSet()

            val entityIdsRequestsAndCachedValues = bucketsCategories.map { bucket ->
                bucket to bucketEntityCachedLoader.getValue(bucket, coroutineScope)
            }
            val cachedValues = entityIdsRequestsAndCachedValues.mapNotNull {
                when (val v = it.second) {
                    is Either.Left -> v.value
                    is Either.Right -> null
                }
            }
            val pendingValues = entityIdsRequestsAndCachedValues.mapNotNull {
                when (val v = it.second) {
                    is Either.Left -> null
                    is Either.Right -> it.first to v.value
                }
            }

            val entitiesIdFlow = MutableStateFlow(cachedValues.flatten().toSet())

            coroutineScope {
                bucketsErrors.update { emptyMap() }

                pendingValues.forEach { (key, req) ->
                    launch {
                        try {
                            pendingBucketsCount.update { it + 1 }
                            pendingBuckets.update { it + key }
                            val loadedEntities = req.await()
                            if (loadedEntities.isOk) {
                                entitiesIdFlow.update {
                                    entitiesIdFlow.value + loadedEntities.value
                                }
                            } else {
                                bucketsErrors.update {
                                    log.e {
                                        "Error loading bucket $key: ${loadedEntities.error}"
                                    }
                                    it + (key to LoaderError.UnknownError)
                                }
                            }
                        } finally {
                            pendingBuckets.update { it - key }
                            pendingBucketsCount.update { it - 1 }
                        }
                    }
                }

                launch {
                    entitiesErrors.update { emptyMap() }
                    entitiesIdFlow
                        .collect { entitiesIds ->
                            resolvedEntityCachedLoader.setActive(entitiesIds)
                            activeEntities.value = entitiesIds.toSet()

                            val entityRequestsAndCachedValues = entitiesIds.map { entityId ->
                                entityId to resolvedEntityCachedLoader.getValue(
                                    entityId,
                                    coroutineScope
                                )
                            }
                            val cachedValues = entityRequestsAndCachedValues.mapNotNull {
                                when (val v = it.second) {
                                    is Either.Left -> v.value
                                    is Either.Right -> null
                                }
                            }
                            val pendingValues = entityRequestsAndCachedValues.mapNotNull {
                                when (val v = it.second) {
                                    is Either.Left -> null
                                    is Either.Right -> it.first to v.value
                                }
                            }
                            val state = cachedValues.associateBy { it.id }.toMutableMap()
                            events.update { state }

                            pendingValues.forEach { (key, pendingRequest) ->
                                launch {
                                    try {
                                        pendingEntitiesCount.update { it + 1 }
                                        pendingEntities.update { it + key }
                                        val loadedEntity = pendingRequest.await()
                                        if (loadedEntity.isErr) {
                                            entitiesErrors.update {
                                                log.e {
                                                    "Error loading entity $key: ${loadedEntity.error}"
                                                }
                                                it + (key to LoaderError.UnknownError)
                                            }
                                        } else {
                                            events.update {
                                                it + (loadedEntity.value.id to loadedEntity.value)
                                            }
                                        }
                                    } finally {
                                        pendingEntities.update { it - key }
                                        pendingEntitiesCount.update { it - 1 }
                                    }
                                }
                            }
                        }
                }
            }

        }

        // Cancel useless buckets and remove them from the loading set
        /*
        val bucketsToCancel = loadingSet - buckets.toSet()
        loadingSet -= bucketsToCancel.keys
        bucketsToCancel.forEach {
            AppLogger.i("EverythingTimeline", "Cancelling ${it.key}")
            it.value.cancel()
        }

        // Get all buckets
        val requestedBuckets = entries.mapValues { (key, value) ->
            if (value != null) {
                coroutineScope.async { value }
            } else {
                val loadingValue = loadingSet[key]
                if (loadingValue != null) {
                    loadingValue
                } else {
                    val job = coroutineScope.async {
                        bucketLoader.loadEvents(key).also {
                            cache.put(key, it)
                        }
                    }
                    job.invokeOnCompletion {
                        AppLogger.i("EverythingTimeline", "Job completed ${key} $it")
                        loadingSet.remove(key)?.cancel()
                    }
                    AppLogger.i("EverythingTimeline", "Loading ${key}")
                    loadingSet[key] = job
                    job
                }
            }
        }

        val allEvents = requestedBuckets
            .values
            .awaitAll()
            .flatten()
            .groupBy {
                it
                // TODO
            }
            .mapValues {
                // it.value.map { it.toModel() }.reduce { acc, model ->
                //model.copy(
                //      properties = model.properties.merge(acc.properties)
                //  )
                //}
                it.value.map {
                    DatedEventModel(
                        id = GroupUid(
                            SpaceUid(UserUid(0), SpaceId(it.id.hashCode().toLong())),
                            GroupId.Root
                        ),
                        properties = EventPropertiesModel(
                            startedAt = GregorianDate(2024, 6, 1), // TODO
                        ),
                        isSynced = false
                    )
                }.reduce { acc, model ->
                    model.copy(
                        properties = model.properties.merge(acc.properties)
                    )
                }
            }


        val limit = 300
        val bucketsCount = 60
        val eventsGroupedByBucket = allEvents.entries.groupBy {
            val startedAtInstant =
                it.value.startedAt.atTime(0, 0).toLongInstant(TimeZone.Companion.UTC)
            (startedAtInstant / limitBucketSize.value).value

        }
        AppLogger.i(
            "EverythingTimeline",
            "Total events: ${allEvents.size}, groups: ${eventsGroupedByBucket.size}"
        )
        val countPerBucket = 10
        val limitedEvents = eventsGroupedByBucket
            .flatMap { (bucket, eventsInBucket) ->
                val countPerBucket = if (bucket == -1L) limit / 2 else countPerBucket

                if (eventsInBucket.size <= countPerBucket) {
                    eventsInBucket
                } else {
                    eventsInBucket.sortedByDescending { it.value.score }
                        .take(countPerBucket)
                }
            }.associate {
                it.key to it.value
            }

        AppLogger.i("EverythingTimeline", "Returning ${limitedEvents.size} events")
        /*  limitedEvents.forEach {
              AppLogger.i("EverythingTimeline", "Event: ${it.value.properties.summary()}")
          }*/

        _events.emit(limitedEvents)
        */
    }

    override suspend fun analyticsEvent(entityId: EntityId) {
        protectAsync {
            api.analyticsEventClicked(entityId)
        }.handleError {
            log.w { "Error sending analytics event clicked: $it" }
        }
    }

    override suspend fun subscribeToNewsletter(email: String): AnyResult<Unit> {
        return protectAsync {
            api.subscribeToNewsletter(email)
        }
    }
}